import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Section, Spinner, type Tone } from '@zt/ui';
import type { ai } from '@zt/core';
import { api, type Jsonify } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';

/**
 * YouTube videoları (F9-10): ürün ve blog videolarının YouTube Data API bilgisi (yalnızca okuma). Anahtar yoksa
 * Kurulum Gerekli; kaldırılmış, gömülemeyen ve Türkiye'de engelli videolar vitrinde gizlenir ve burada uyarılır.
 */

type R<F extends (...a: never[]) => unknown> = Jsonify<Awaited<ReturnType<F>>>;
type Overview = R<typeof ai.youtube.youtubeOverview>;
type SyncResult = R<typeof ai.youtube.syncYoutubeNow>;
type Video = Overview['videos'][number];

const TONE: Record<string, Tone> = {
  available: 'moss',
  processing: 'slate',
  not_embeddable: 'amber',
  region_blocked: 'amber',
  unavailable: 'rose',
  unknown: 'neutral',
};
const PROBLEM = new Set(['not_embeddable', 'region_blocked', 'unavailable']);

const duration = (s: number) =>
  s >= 3600
    ? `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
    : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export function YoutubePage() {
  const t = useT();
  const q = useQuery({ queryKey: ['youtube'], queryFn: () => api.get<Overview>('/v1/admin/youtube') });
  const d = q.data;
  return (
    <PageFrame helpKey="page.ai_youtube">
      <p className="max-w-[70ch] text-sm text-ink-2">{t('ai.youtube.lead')}</p>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <ConnectionSection d={d} />
          <VideosSection videos={d.videos} />
        </>
      )}
    </PageFrame>
  );
}

function ConnectionSection({ d }: { d: Overview }) {
  const t = useT();
  const { can } = usePermissions();
  const { formatDate } = useI18n();
  const sync = useApiMutation(() => api.post<SyncResult>('/v1/admin/youtube/sync'), {
    invalidate: [['youtube']],
    success: (r) => t('ai.youtube.synced', { count: r.videos }),
  });
  return (
    <Section title={t('ai.youtube.connectionTitle')} action={<HelpTip k="youtube.connection" />}>
      {!d.configured ? (
        <div className="space-y-2 text-sm">
          <p className="flex flex-wrap items-center gap-2">
            <Badge tone="amber">{t('ai.availability.not_configured')}</Badge>
            {d.connection?.status === 'ERROR'
              ? t('ai.youtube.connectionError')
              : t('ai.youtube.notConfigured')}
          </p>
          {can('org.integration.read') && (
            <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
              {t('ai.youtube.toIntegrations')}
            </Link>
          )}
        </div>
      ) : (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <Badge tone="moss">{t('ai.youtube.connected')}</Badge>
          {d.connection?.lastSyncAt
            ? t('ai.youtube.lastSync', { date: formatDate(d.connection.lastSyncAt, true) })
            : t('ai.youtube.neverSynced')}
        </p>
      )}
      {can('seo.write') && (
        <span className="mt-3 inline-flex items-center gap-1">
          <Button disabled={!d.configured} loading={sync.isPending} onClick={() => sync.mutate(undefined)}>
            {t('ai.youtube.syncNow')}
          </Button>
          <HelpTip k="youtube.sync" />
        </span>
      )}
      {d.logs && d.logs.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium">{t('ai.youtube.logsTitle')}</h3>
          <ul
            className="mt-1 divide-y divide-line rounded-md border border-line text-sm"
            aria-label={t('ai.youtube.logsTitle')}
          >
            {d.logs.map((l, i) => {
              const s = (l.summary ?? {}) as { code?: string; videos?: number; requests?: number };
              return (
                <li key={i} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className="text-ink-3 tabular-nums">{formatDate(l.createdAt, true)}</span>
                  <Badge tone={l.outcome === 'success' ? 'moss' : 'rose'}>
                    {t(`ai.youtube.outcome.${l.outcome}`)}
                  </Badge>
                  <span>
                    {l.outcome === 'success'
                      ? t('ai.youtube.logOk', { videos: s.videos ?? 0, requests: s.requests ?? 0 })
                      : t(`errors.${s.code ?? 'youtube_unreachable'}`)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Section>
  );
}

function VideosSection({ videos }: { videos: Video[] }) {
  const t = useT();
  const { formatDate } = useI18n();
  const [onlyIssues, setOnlyIssues] = useState(false);
  const rows = videos.filter((v) => !onlyIssues || (v.status && PROBLEM.has(v.status)));
  const problems = videos.filter((v) => v.status && PROBLEM.has(v.status)).length;
  return (
    <Section title={t('ai.youtube.videosTitle')} action={<HelpTip k="youtube.videos" />}>
      {!videos.length ? (
        <p className="text-sm text-ink-2">{t('ai.youtube.noVideos')}</p>
      ) : (
        <>
          <p className="mb-2 text-sm text-ink-2">
            {t('ai.youtube.summary', { count: videos.length, problems })}
          </p>
          <label className="mb-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
            {t('ai.youtube.onlyIssues')}
          </label>
          <ul className="space-y-2" aria-label={t('ai.youtube.videosTitle')}>
            {rows.map((v) => (
              <li key={v.videoId} className="rounded-md border border-line p-3 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{v.title}</span>
                  <Badge tone={TONE[v.status ?? 'unknown']}>
                    {t(`ai.youtube.status.${v.status ?? 'unknown'}`)}
                  </Badge>
                </p>
                <p className="mt-1 text-ink-3">
                  {[
                    v.channelTitle,
                    v.durationSeconds !== null ? duration(v.durationSeconds) : null,
                    v.publishedAt ? t('ai.youtube.published', { date: formatDate(v.publishedAt) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  {v.channelTitle || v.durationSeconds !== null || v.publishedAt ? ' · ' : ''}
                  <a
                    href={`https://www.youtube.com/watch?v=${encodeURIComponent(v.videoId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pine underline underline-offset-2"
                  >
                    {t('ai.youtube.open')}
                  </a>
                </p>
                {v.status && PROBLEM.has(v.status) && (
                  <p className="mt-1 text-amber">{t(`ai.youtube.problem.${v.status}`)}</p>
                )}
                <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span className="text-ink-3">{t('ai.youtube.usedBy')}</span>
                  {v.usedBy.map((u) =>
                    u.type === 'product' ? (
                      <Link
                        key={`${u.type}:${u.id}`}
                        to="/urunler/$id"
                        params={{ id: u.id }}
                        className="text-pine underline underline-offset-2"
                      >
                        {t('ai.youtube.product', { name: u.name })}
                      </Link>
                    ) : (
                      <Link
                        key={`${u.type}:${u.id}`}
                        to="/blog/$id"
                        params={{ id: u.id }}
                        className="text-pine underline underline-offset-2"
                      >
                        {t('ai.youtube.post', { name: u.name })}
                      </Link>
                    ),
                  )}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}
